import { Loader } from '@googlemaps/js-api-loader';
import { useState, useEffect, createRef, RefObject } from 'react';
import { useTranslation } from 'next-i18next';
import { useSearchParams } from 'next/navigation';
import GoogleMap from '@features/showrooms/sections/ShowroomsSection/Components/GoogleMap/GoogleMap';
import SearchShowroom from '@features/showrooms/sections/ShowroomsSection/Components/SearchShowroom/SearchShowroom';
import styles from '@features/showrooms/sections/ShowroomsSection/ShowroomsSection.styles';
import nextShowroomApi from '@features/api/nextApiService/nextShowroomApi';
import ShowroomInfo from '@features/showrooms/sections/ShowroomsSection/Components/ShowroomInfo/ShowroomInfo';
import ServiceFiltersInfo from '@features/showrooms/sections/ShowroomsSection/Components/ServiceFiltersInfo/ServiceFiltersInfo';
import ShowroomFiltersInfo from '@features/showrooms/sections/ShowroomsSection/Components/ShowroomFiltersInfo/ShowroomFiltersInfo';
import Aside from '@components/Aside/Aside';
import {
  IPointOfPresence,
  IGoogleMapLocation,
  IUserLocation,
  IShoowroomFilters,
  IPointOfPresenceWithDetails,
  IPointOfPresenceGroupNames,
} from '@features/checkout/typescript';
import {
  findPlaceFromQuery,
  foundShowroomsByLocation,
  sortShowroomsByCity,
  findShowroomLocationByCity,
} from '@utils/showroom';
import getPublicEnv from '@utils/getPublicEnv';
import { IGeoData } from '@features/showrooms/typescript/index';
import layoutUiState from '@features/layout/mobX/LayoutUiState';

type PropsType = {
  countryCode: string;
  showPartnersLogos: boolean;
};

let showroomInfoTimeOut: NodeJS.Timeout | null;
let isDesktopTimeOut: NodeJS.Timeout | null;

const ShowroomsSection = ({ countryCode, showPartnersLogos = false }: PropsType) => {
  const { t } = useTranslation('common');
  const [foundShowrooms, setFoundShowrooms] = useState<Array<IPointOfPresence>>([]);
  const [showroomsNames, setShowroomsNames] = useState<IPointOfPresenceGroupNames>({});
  const [useShowroomsFilters, setUseShowroomsFilters] = useState<boolean>(false);
  const [disabledShowrooms, setDisabledShowrooms] = useState<Array<IPointOfPresence>>([]);
  const [allShowrooms, setAllShowrooms] = useState<Array<IPointOfPresence>>([]);
  const [showroomInfo, setShowroomInfo] = useState<IPointOfPresence | IPointOfPresenceWithDetails | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [info, setInfo] = useState<string>('');
  const params = useSearchParams();

  const { getLayoutBarsTotalHeight } = layoutUiState;

  const loader = new Loader({
    apiKey: getPublicEnv('NEXT_PUBLIC_GOOGLE_MAP_API_KEY'),
    version: 'weekly',
    libraries: ['places'],
  });
  const mapSection: RefObject<HTMLInputElement | null> = createRef();

  const getClearLocation = (): IGoogleMapLocation => ({
    lat: null,
    lng: null,
  });

  const getClearUserLocation = (): IUserLocation => ({
    location: getClearLocation(),
    country: null,
    countryCode: countryCode || null,
    city: null,
    postcode: null,
  });

  const getClearFilters = (): IShoowroomFilters => [
    [false, false, false, false], // zostawiam filtr z voxboxem - może się jeszcze przydać jak uzupełnią filtry w salonach
    [false, false, false, false],
  ];

  const [userLocation, setUserLocation] = useState<IUserLocation>(getClearUserLocation());
  const [searchLocation, setSearchLocation] = useState<IGoogleMapLocation>(getClearLocation());
  const [searchWord, setSearchWord] = useState<string>('');
  const [startView, setStartView] = useState<boolean>(true);
  const [filters, setFilters] = useState<IShoowroomFilters>(getClearFilters());
  const [activeFilters, setActiveFilters] = useState<boolean>(true);
  const [activeShowroomInfo, setActiveShowroomInfo] = useState<boolean>(false);
  const [activeShowroomFiltersInfo, setActiveShowroomFiltersInfo] = useState<boolean>(false);
  const [activeServiceFiltersInfo, setActiveServiceFiltersInfo] = useState<boolean>(false);
  const [activeBookmarksIndex, setActiveBookmarksIndex] = useState<number>(0);
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  const [showroomsLoaded, setShowroomsLoaded] = useState<boolean>(false);

  const clearTimeOut = (timeOut: NodeJS.Timeout | null) => {
    if (timeOut) {
      clearTimeout(timeOut);
    }
  };

  const showSohowroomList = (newSearchLocation: IGoogleMapLocation | null = null) => {
    if ((newSearchLocation?.lat && newSearchLocation.lng) || (searchLocation?.lat && searchLocation.lng))
      setStartView(false);
  };

  const handleInfoTime = (time = 2000) => {
    if (info.length > 0) {
      clearTimeOut(showroomInfoTimeOut);
      showroomInfoTimeOut = setTimeout(() => {
        setInfo('');
      }, time);
    }
  };

  const setShoowroomsByFilters = (
    _foundShowrooms: IPointOfPresence[],
    _disabledShowrooms: IPointOfPresence[],
    _filters: IShoowroomFilters,
    newSearchLocation: IGoogleMapLocation | null = null,
  ) => {
    const newfoundShowrooms: IPointOfPresence[] = [];
    const newDisabledShowrooms: IPointOfPresence[] = _disabledShowrooms;

    _foundShowrooms.forEach((showroom) => {
      let push = false;
      const services = showroom?.services ? showroom.services.map((item) => item.code) : [];

      if (showroom?.location?.latitude && showroom?.location?.longitude) {
        if (_filters[0].includes(true)) {
          push = push || (_filters[0][0] && showroom?.group?.code === 'VOX_INTERIORS');
          push =
            push ||
            (_filters[0][1] &&
              (showroom?.group?.code === 'VOX_FURNITURES' ||
                showroom?.group?.code === 'VOX_INTERIORS' ||
                showroom?.group?.code === 'VOXBOX_STUDIO'));
          push =
            push ||
            (_filters[0][2] && (showroom?.group?.code === 'VOX_DIP' || showroom?.group?.code === 'VOX_INTERIORS'));
        } else {
          push = true;
        }

        if (push && _filters[1].includes(true)) {
          let notCondition = false;
          if (_filters[1][0]) notCondition = !services.includes('VOXBOX_AVAILABLE');
          if (!notCondition && _filters[1][1]) notCondition = !services.includes('VOXBOX_VR_AVAILABLE');
          // if (!notCondition && _filters[1][2]) notCondition = !services.includes('MATERIAL_LIBRARY');
          // if (!notCondition && _filters[1][3]) notCondition = !services.includes('DESIGNER_ZONE');
          push = !notCondition;
        }

        if (push) {
          newfoundShowrooms.push(showroom);
        } else {
          newDisabledShowrooms.push(showroom);
        }
      }
    });
    showSohowroomList(newSearchLocation);
    setFoundShowrooms(newfoundShowrooms);
    setDisabledShowrooms(newDisabledShowrooms);
  };

  const handleFoundShowrooms = (location: IGoogleMapLocation, newFilters: IShoowroomFilters) => {
    if (location.lat && location.lng) {
      const { showrooms: newfoundShowrooms, newDisabledShowrooms } = foundShowroomsByLocation(location, allShowrooms);
      setShoowroomsByFilters(
        newfoundShowrooms as IPointOfPresence[],
        newDisabledShowrooms as IPointOfPresence[],
        newFilters,
        location,
      );
    }
  };

  const searchShowrooms = async (
    newSearchWord: string | null,
    newLocation: IGoogleMapLocation | null = null,
    newMap: google.maps.Map | null = null,
  ) => {
    if (allShowrooms.length > 0) {
      const googleMap = newMap || map;
      if (googleMap && newSearchWord) {
        setSearchLocation(getClearLocation);
        const resultLocationBycityName = findShowroomLocationByCity(allShowrooms, newSearchWord);
        if (resultLocationBycityName?.lat && resultLocationBycityName?.lng) {
          setSearchLocation(resultLocationBycityName);
          searchShowrooms(null, resultLocationBycityName);
        } else {
          const resultLocation = await findPlaceFromQuery(googleMap, newSearchWord);
          if (resultLocation) {
            setSearchLocation(resultLocation);
            searchShowrooms(null, resultLocation);
          } else {
            setStartView(false);
            setActiveFilters(false);
            setFoundShowrooms([]);
            setDisabledShowrooms([...allShowrooms]);
          }
        }
      } else if (newLocation) {
        setStartView(false);
        setActiveFilters(false);
        setSearchLocation(newLocation);
        handleFoundShowrooms(newLocation, filters);
      } else {
        setStartView(true);
        setActiveFilters(true);
        setSearchLocation(getClearLocation);
        setFoundShowrooms(sortShowroomsByCity([...allShowrooms]));
        setDisabledShowrooms([]);
      }
      handleInfoTime(0);
    } else {
      setInfo(t('showroomsWait'));
    }
    if (newSearchWord) setSearchWord(newSearchWord);
  };

  const handleUserCountryLocation = (geoData: IGeoData) => {
    if (!userLocation.country) {
      if (geoData?.country) {
        setUserLocation({
          ...userLocation,
          countryCode: geoData.country,
        });
      } else if (countryCode) {
        setUserLocation({
          ...userLocation,
          countryCode,
        });
      }
    }
  };

  const afterShowroomsfetch = () => {
    if (searchWord.length) {
      searchShowrooms(searchWord);
    } else if (filters[0].includes(true) || filters[1].includes(true)) {
      setShoowroomsByFilters(sortShowroomsByCity([...allShowrooms]), [], filters);
    }
  };

  const fetchShowroomsAndGeoData = async () => {
    const fetchShowrooms = await nextShowroomApi.getAllShowroomsAndGeoData();
    if (fetchShowrooms?.showrooms && allShowrooms.length === 0) {
      const sortShoowrooms = sortShowroomsByCity(fetchShowrooms?.showrooms);
      setAllShowrooms(sortShoowrooms);
      setFoundShowrooms(sortShoowrooms);
      setShowroomsNames(fetchShowrooms?.showroomsNames);
      handleInfoTime(0);
      afterShowroomsfetch();
      handleUserCountryLocation(fetchShowrooms?.geoData);
    }
  };

  const handleFilters = (newFilters: IShoowroomFilters) => {
    if (allShowrooms.length > 0) {
      if (searchLocation.lat && searchLocation.lng) {
        handleFoundShowrooms(searchLocation, newFilters);
      } else {
        setShoowroomsByFilters([...allShowrooms], [], newFilters);
      }
      handleInfoTime(0);
    } else {
      setInfo(t('showroomsWait'));
    }
    setFilters(newFilters);
  };

  const setFilterIndex = (index: 0 | 1 | 2 | 3) => {
    const newFilters = getClearFilters();
    newFilters[0][index] = true;
    setFoundShowrooms(allShowrooms);
    handleFilters(newFilters);
    if (mapSection?.current) mapSection.current.scrollIntoView({ behavior: 'smooth' });
  };
  const hendleUrlFiltersParameters = () => {
    if (params && params.get('filter')) {
      switch (params.get('filter')) {
        case 'VOX_INTERIORS':
          setFilterIndex(0);
          break;
        case 'VOX_FURNITURES':
          setFilterIndex(1);
          break;
        case 'VOX_DIP':
          setFilterIndex(2);
          break;
        case 'VOXBOX_AVAILABLE':
          setFilterIndex(3);
          break;
        default:
          break;
      }
    }
  };

  const handleShowroomInfo = (
    newShowroomUuid: string | null,
    newShowroomInfo: IPointOfPresence | null | IPointOfPresenceWithDetails = null,
  ) => {
    let newShowroomInfoToSet: IPointOfPresence | null | undefined = newShowroomInfo;
    if (!newShowroomInfoToSet) {
      newShowroomInfoToSet = allShowrooms.find(({ uuid }) => uuid === newShowroomUuid);
    }

    if (newShowroomInfoToSet) {
      setShowroomInfo(newShowroomInfoToSet);
      setActiveShowroomInfo(true);
    }
  };

  const onCloseAsideShowroomInfo = () => {
    setActiveShowroomInfo(false);
    setShowroomInfo(null);
  };

  const handleShowroomPage = () => {
    setActiveShowroomInfo(false);
  };

  const handleIsDesktop = () => {
    const newIsDesktop = window.innerWidth > 1280;
    setIsDesktop(newIsDesktop);
    if (newIsDesktop) {
      setActiveBookmarksIndex(0);
    }
  };

  const handleIsDesktopTime = (time = 200) => {
    clearTimeOut(isDesktopTimeOut);
    isDesktopTimeOut = setTimeout(() => {
      handleIsDesktop();
    }, time);
  };

  const onResize = () => {
    handleIsDesktopTime();
  };

  const getSizeMap = (): 'small' | 'medium' | 'large' => {
    if (!isDesktop && !startView && activeFilters) {
      return 'small';
    }
    if (!isDesktop && startView) {
      return 'medium';
    }
    return 'large';
  };

  useEffect(() => {
    fetchShowroomsAndGeoData();
    if (allShowrooms.length) {
      setShowroomsLoaded(true);
    }
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeOut(isDesktopTimeOut);
      clearTimeOut(showroomInfoTimeOut);
    };
  }, [allShowrooms]);

  useEffect(() => {
    hendleUrlFiltersParameters();
  }, [params]);

  useEffect(() => {
    setUseShowroomsFilters(getPublicEnv('NEXT_PUBLIC_USE_SHOWROOMS_FILTERS') !== 'false');
  });

  return (
    <section>
      <div sx={styles.searchContainer(getLayoutBarsTotalHeight())} ref={mapSection}>
        <div sx={styles.mapCol}>
          <GoogleMap
            map={map}
            setMap={setMap}
            loader={loader}
            active={isDesktop || activeBookmarksIndex === 0}
            size={getSizeMap()}
            points={foundShowrooms}
            point={showroomInfo}
            disabledPoints={disabledShowrooms}
            userLocation={userLocation}
            searchLocation={searchLocation}
            setPointUuid={handleShowroomInfo}
          />
        </div>
        <div sx={styles.searchCol}>
          <SearchShowroom
            getClearUserLocation={getClearUserLocation}
            userLocation={userLocation}
            setUserLocation={setUserLocation}
            loader={loader}
            showrooms={foundShowrooms}
            searchShowrooms={searchShowrooms}
            startView={startView}
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            filters={filters}
            handleFilters={handleFilters}
            info={info}
            setInfo={setInfo}
            setActiveShowroomFiltersInfo={setActiveShowroomFiltersInfo}
            setActiveServiceFiltersInfo={setActiveServiceFiltersInfo}
            setShowroomUuid={handleShowroomInfo}
            activeBookmarksIndex={activeBookmarksIndex}
            setActiveBookmarksIndex={setActiveBookmarksIndex}
            isDesktop={isDesktop}
            showroomsLoaded={showroomsLoaded}
            map={map}
            useShowroomsFilters={useShowroomsFilters}
            showroomsNames={showroomsNames}
            showPartnersLogos={showPartnersLogos}
          />
        </div>
      </div>
      {!activeShowroomFiltersInfo && !activeServiceFiltersInfo && (
        <Aside
          data-cy="AsideShowroomInfo"
          active={activeShowroomInfo}
          onCloseAside={() => onCloseAsideShowroomInfo()}
          buttonCloseVariantColor="dark"
          buttonCloseVariantBackgroundColor="white"
          useCurtain={false}
          size="large"
          key="AsideShowroomInfo"
        >
          {showroomInfo !== null && (
            <ShowroomInfo
              showroom={showroomInfo}
              setShowroomInfo={handleShowroomInfo}
              handleShowroomPage={handleShowroomPage}
              userLocation={userLocation}
            />
          )}
        </Aside>
      )}
      {!activeShowroomInfo && !activeServiceFiltersInfo && (
        <Aside
          data-cy="AsideShowroomFiltersInfo"
          active={activeShowroomFiltersInfo}
          onCloseAside={() => setActiveShowroomFiltersInfo(false)}
          key="AsideShowroomFiltersInfo"
        >
          <ShowroomFiltersInfo />
        </Aside>
      )}
      {!activeShowroomInfo && !activeShowroomFiltersInfo && (
        <Aside
          data-cy="AsideServiceFiltersInfo"
          active={activeServiceFiltersInfo}
          onCloseAside={() => setActiveServiceFiltersInfo(false)}
          key="AsideServiceFiltersInfo"
        >
          <ServiceFiltersInfo />
        </Aside>
      )}
    </section>
  );
};

export default ShowroomsSection;
